/**
 * The style bar's arranging tools: a shape's outline; with several items, lining them up,
 * spreading them out and grouping; front, back and duplicate.
 */
import { For, type JSX } from "solid-js";
import { useApp } from "../../../../app/context";
import type { Alignment } from "../../../../state/board";
import { SHAPES } from "../look";
import type { Bar } from "./bar";

/** The ways to line several items up, with their words and signs. */
const ALIGNS: { how: Alignment; label: string; sign: string }[] = [
  { how: "left", label: "Line up left edges", sign: "⇤" },
  { how: "centre", label: "Line up centres", sign: "↔" },
  { how: "right", label: "Line up right edges", sign: "⇥" },
  { how: "top", label: "Line up tops", sign: "⤒" },
  { how: "middle", label: "Line up middles", sign: "↕" },
  { how: "bottom", label: "Line up bottoms", sign: "⤓" },
];

/** A shape's outline. */
export function ShapeTools(props: { bar: Bar }): JSX.Element {
  const { board } = useApp();
  return (
    <select id="board-shape" aria-label="Shape" value={props.bar.value("shape") || "rectangle"} onChange={(e) => void board.setStyle("shape", e.currentTarget.value)}>
      <For each={SHAPES}>{(s) => <option value={s}>{s}</option>}</For>
    </select>
  );
}

/** Lining up, spreading out and grouping several items. */
export function ManyTools(): JSX.Element {
  const { board } = useApp();
  return (
    <>
      <span class="board-sep" />
      <For each={ALIGNS}>
        {(a) => (
          <button type="button" data-board-action={`align-${a.how}`} aria-label={a.label} title={a.label} onClick={() => void board.align(a.how)}>
            {a.sign}
          </button>
        )}
      </For>
      <button type="button" data-board-action="spread-x" aria-label="Spread out across" title="Spread out across (three or more)" onClick={() => void board.distribute("x")}>
        ⋯
      </button>
      <button type="button" data-board-action="spread-y" aria-label="Spread out down" title="Spread out down (three or more)" onClick={() => void board.distribute("y")}>
        ⋮
      </button>
      <button type="button" data-board-action="group" title="Group (Ctrl+G)" onClick={() => void board.group()}>
        Group
      </button>
    </>
  );
}

/** Front, back and duplicate. */
export function OrderTools(): JSX.Element {
  const { board } = useApp();
  return (
    <>
      <span class="board-sep" />
      <button type="button" data-board-action="front" title="Bring to front (Ctrl+])" onClick={() => void board.order("front")}>
        Front
      </button>
      <button type="button" data-board-action="back" title="Send to back (Ctrl+[)" onClick={() => void board.order("back")}>
        Back
      </button>
      <button type="button" data-board-action="duplicate" title="Duplicate (Ctrl+D)" onClick={() => void board.duplicate()}>
        Duplicate
      </button>
    </>
  );
}
